import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";

const EMPTY = { name: "", role: "", bio: "", image: "", published: true, order: 0 };

function ImageInput({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="eyebrow text-ink/55">Portrait</label>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-20 h-20 bg-mist/40 border border-mist flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={18} className="text-ink/30" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3">
            <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-pick`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper">
              Choose from library
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-[12px] tracking-[0.14em] uppercase text-ink/50 hover:text-copper inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>
          <input
            type="text"
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste an image URL"
            className="input-line text-[13px]"
          />
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
}

function Row({ value, onChange, onSave, onDelete, saving }) {
  return (
    <div data-testid={`team-row-${value.id || "new"}`} className="border border-mist p-6 space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="eyebrow text-ink/55">Name</label>
          <input data-testid={`team-name-${value.id || "new"}`} type="text" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="input-line mt-3" />
        </div>
        <div className="md:col-span-2">
          <label className="eyebrow text-ink/55">Role</label>
          <input data-testid={`team-role-${value.id || "new"}`} type="text" value={value.role} onChange={(e) => onChange({ ...value, role: e.target.value })} className="input-line mt-3" />
        </div>
      </div>
      <div>
        <label className="eyebrow text-ink/55">Bio (optional, kept short)</label>
        <textarea data-testid={`team-bio-${value.id || "new"}`} value={value.bio} onChange={(e) => onChange({ ...value, bio: e.target.value })} rows={2} className="input-line mt-3 resize-y" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ImageInput
          value={value.image}
          onChange={(url) => onChange({ ...value, image: url })}
          testid={`team-image-${value.id || "new"}`}
        />
        <div>
          <label className="eyebrow text-ink/55">Order</label>
          <input data-testid={`team-order-${value.id || "new"}`} type="number" value={value.order} onChange={(e) => onChange({ ...value, order: parseInt(e.target.value, 10) || 0 })} className="input-line mt-3" />
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/65">
          <input type="checkbox" checked={value.published} onChange={(e) => onChange({ ...value, published: e.target.checked })} data-testid={`team-published-${value.id || "new"}`} /> Published
        </label>
        <div className="flex items-center gap-3">
          {onDelete && (
            <button type="button" onClick={onDelete} className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2" data-testid={`team-delete-${value.id}`}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button type="button" onClick={onSave} disabled={saving} data-testid={`team-save-${value.id || "new"}`} className="btn-primary text-[12px] py-2 px-5 disabled:opacity-40">
            {saving ? "Saving…" : value.id ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamList() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/team").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusyId("new");
    try {
      await api.post("/admin/team", draft);
      setDraft({ ...EMPTY });
      await load();
      toast.success("Team member added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setBusyId(null);
    }
  };

  const update = async (item) => {
    setBusyId(item.id);
    try {
      const { id, created_at, updated_at, ...payload } = item;
      await api.put(`/admin/team/${id}`, payload);
      await load();
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    try {
      await api.delete(`/admin/team/${id}`);
      await load();
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-team">
      <p className="eyebrow">Content</p>
      <h1 className="mt-3 font-serif text-4xl text-jade font-light">Team</h1>
      <p className="mt-3 text-ink/65 text-[14px] font-light max-w-2xl">
        Specialists shown on the About page. Order controls display sequence (lower numbers appear first).
      </p>

      <div className="mt-12 space-y-6">
        {items.map((t) => (
          <Row
            key={t.id}
            value={t}
            onChange={(v) => setItems((arr) => arr.map((x) => (x.id === t.id ? v : x)))}
            onSave={() => update(items.find((x) => x.id === t.id))}
            onDelete={() => remove(t.id)}
            saving={busyId === t.id}
          />
        ))}
      </div>

      <div className="mt-12">
        <p className="eyebrow text-copper inline-flex items-center gap-2"><Plus size={14} /> Add a new team member</p>
        <div className="mt-5">
          <Row value={draft} onChange={setDraft} onSave={create} saving={busyId === "new"} />
        </div>
      </div>
    </div>
  );
}
